import dynamic from 'next/dynamic';
import { GetServerSideProps } from 'next';
import FeaturedLastNews from '@components/Home/FeaturedLastNews';
import { api, mimir, site } from '@/services/api';
import { TimeLine } from '@/types/types';
import { AdContainer } from '@/components/Layout/ItemListLastNews/styles';
import PodcastItem, { PodcastData } from '@/components/Layout/Podcast';
import { NavBarContentData, TimeLineApi } from '@/shared/types/home.types';
import getConcurrentPromises from '@/utils/getConcurrentPromises';
import { SchemaType } from '@/components/Internals/InternalSchema';
import Layout from '@/components/Layout';

export type Vitrine = {
  tipo: string;
  titulo: string;
  categoria: string;
  autor: string;
  url: string;
  imagem: {
    url: string;
    blurhash: string;
  };
  data: string;
  idade: string;
};

export type HeadTags = {
  tag: string;
  content: string;
  attr: {
    rel: string;
    href: string;
    type: string | undefined;
    title: string | undefined;
    name: string | undefined;
    content: string | undefined;
    property: string | undefined;
    'http-equiv': string | undefined;
  };
};

interface LastNewsItems {
  itens: TimeLine[];
  paginacao: string;
}

interface FeaturedNewsType {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navBarData: any;
  tagsData: HeadTags[];
  homeData: {
    porta101: PodcastData;
    canaltech: PodcastData;
    timeline: LastNewsItems;
  };
  segmentacoes: [];
  timelineData: {
    nome: string;
    titulo: string;
    vitrine: Vitrine[];
    timeline: LastNewsItems;
  };
  schema?: SchemaType;
}

interface FeaturedNewsApi {
  data: {
    vitrine: Vitrine[];
    timeline: TimeLine[];
  };
  ssr: HeadTags[];
  segmentacoes: [];
  headers: {
    'Cache-control': string;
    Expires: string;
  };
  schema?: SchemaType;
}

export default function PodcastRoot({ homeData, navBarData, tagsData, segmentacoes, timelineData, schema = null }: FeaturedNewsType): JSX.Element {
  const AdsTypeGenerators = dynamic(() => import('@/components/Ads/Google/AdsTypeGenerators'), { ssr: false });

  const title = `Todos os Podcasts`;

  // O dataLayer é necessário para o GTM renderizar os ads na página.
  return (
    <Layout layoutData={{ segmentacoes, tagsData, navBarData, schema }} dataLayer={{ contentType: 'list' }}>
      <PodcastItem podcastData={homeData} />
      <AdContainer noPadding billboard>
        <AdsTypeGenerators type="billboard/main/listing" />
      </AdContainer>
      <FeaturedLastNews featuredLastNewsData={timelineData.timeline} rootPage="podcast" textTitle={title} headingTag="h1" route="podcast" routeType="tipo" />
    </Layout>
  );
}

export const getServerSideProps: GetServerSideProps = async context => {
  const { host } = context.req.headers;

  const mainReq = { promise: site.get<FeaturedNewsApi>(`/podcast/`), link: `/podcast/` };
  const timelineReq = { promise: mimir.get<TimeLineApi>(`/timelines/ultimas/tipo/podcast`), link: `/timelines/ultimas/tipo/podcast` };
  const responseMenuReq = { promise: api.get<NavBarContentData>(`/componentes/menu/`), link: `/componentes/menu/` };

  const { promiseData, data, statusCode } = await getConcurrentPromises({
    mainPromise: mainReq,
    navBarPromise: responseMenuReq,
    timelinePromise: timelineReq,
    host,
  });

  if (data.redirect || data.notFound) return data;

  const homeData = data.data;
  const schema = data?.schema;
  const navBarData: NavBarContentData = promiseData[responseMenuReq.link].data.data;
  const tagsData = data.ssr;
  const { segmentacoes } = data;
  const timelineData = promiseData[timelineReq.link].data.data;

  context.res.setHeader('Cache-Control', data.headers['Cache-control']);
  context.res.setHeader('Expires', data.headers.Expires);
  context.res.statusCode = statusCode;

  return {
    props: {
      tagsData,
      homeData,
      schema,
      navBarData,
      segmentacoes,
      timelineData,
    },
  };
};
